import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/CrmAuth";
import { OrganizationProvider } from "@/context/Organization";
import CrmLayout from "@/crm/CrmLayout";
import { Loader } from "@/crm/ui";

import Login from "@/crm/pages/Login";
import Dashboard from "@/crm/pages/Dashboard";
import Companies from "@/crm/pages/Companies";
import CompanyDetail from "@/crm/pages/CompanyDetail";
import Campaigns from "@/crm/pages/Campaigns";
import CampaignDetail from "@/crm/pages/CampaignDetail";
import Pipeline from "@/crm/pages/Pipeline";
import Enrichment from "@/crm/pages/Enrichment";
import Activities from "@/crm/pages/Activities";
import Tasks from "@/crm/pages/Tasks";
import Calendar from "@/crm/pages/Calendar";
import ImportExport from "@/crm/pages/ImportExport";
import Team from "@/crm/pages/Team";
import Reports from "@/crm/pages/Reports";
import SettingsPage from "@/crm/pages/SettingsPage";
import OrganizationSettings from "@/crm/pages/OrganizationSettings";
import Trash from "@/crm/pages/Trash";
import Exclusions from "@/crm/pages/Exclusions";
import PromptTemplates from "@/crm/pages/PromptTemplates";
import Comparison from "@/crm/pages/Comparison";
import Telephony from "@/crm/pages/Telephony";
import Contacts from "@/crm/pages/Contacts";
import ContactDetail from "@/crm/pages/ContactDetail";
import EmailTemplates from "@/crm/pages/EmailTemplates";
import Workflows from "@/crm/pages/Workflows";
import AdminDebug from "@/crm/pages/AdminDebug";
import Solutions from "@/crm/pages/Solutions";
import CallCampaigns from "@/crm/pages/CallCampaigns";
import CallCampaignDetail from "@/crm/pages/CallCampaignDetail";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loader />;
  if (!user) return <Navigate to="/connexion" replace />;
  return children;
}

function RoleRoute({ roles, children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loader />;
  if (!user || !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

// Le Mode Debug vivait sur /admin/debug avant d'être rattaché aux Paramètres :
// les favoris et liens copiés continuent d'aboutir, querystring comprise
// (préréglage ?company_id=… de la carte d'activation).
function LegacyCallCampaignRedirect() {
  const { id } = useParams();
  return <Navigate to={`/campagnes/appel/${id}`} replace />;
}

function LegacyDebugRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/parametres/debug${search}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      {/* Organisation active et permissions : sous l'authentification, dont
          elles dépendent, au-dessus des écrans, qui s'en servent (plan V4 §40). */}
      <OrganizationProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/connexion" element={<Login />} />
          <Route element={<Protected><CrmLayout /></Protected>}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/entreprises" element={<Companies />} />
            <Route path="/entreprises/:id" element={<CompanyDetail />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/contacts/:id" element={<ContactDetail />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/campagnes" element={<RoleRoute roles={["admin"]}><Campaigns /></RoleRoute>} />
            {/* Sous-menu Campagnes : /campagnes/appel avant /campagnes/:id — le
                segment fixe l'emporte sur le paramètre, et aucune campagne
                d'extraction ne porte l'identifiant « appel ». */}
            <Route path="/campagnes/appel" element={<CallCampaigns />} />
            <Route path="/campagnes/appel/:id" element={<CallCampaignDetail />} />
            <Route path="/campagnes/:id" element={<RoleRoute roles={["admin"]}><CampaignDetail /></RoleRoute>} />
            <Route path="/campagnes-appel" element={<Navigate to="/campagnes/appel" replace />} />
            <Route path="/campagnes-appel/:id" element={<LegacyCallCampaignRedirect />} />
            <Route path="/enrichissement" element={<Enrichment />} />
            <Route path="/activites" element={<Activities />} />
            <Route path="/taches" element={<Tasks />} />
            <Route path="/calendrier" element={<Calendar />} />
            <Route path="/import" element={<RoleRoute roles={["admin", "manager"]}><ImportExport /></RoleRoute>} />
            <Route path="/oppositions" element={<Exclusions />} />
            <Route path="/telephonie" element={<Telephony />} />
            <Route path="/modeles-ia" element={<RoleRoute roles={["admin", "manager"]}><PromptTemplates /></RoleRoute>} />
            <Route path="/templates-emails" element={<RoleRoute roles={["admin", "manager"]}><EmailTemplates /></RoleRoute>} />
            <Route path="/automatisations" element={<RoleRoute roles={["admin", "manager"]}><Workflows /></RoleRoute>} />
            <Route path="/equipe" element={<Team />} />
            <Route path="/parametres/organisation" element={<OrganizationSettings />} />
            {/* Corbeille des fiches : admin de plateforme (le serveur refuse les autres). */}
            <Route path="/corbeille" element={<RoleRoute roles={["admin"]}><Trash /></RoleRoute>} />
            <Route path="/rapports" element={<Reports />} />
            <Route path="/comparaison" element={<RoleRoute roles={["admin", "manager"]}><Comparison /></RoleRoute>} />
            <Route path="/parametres" element={<SettingsPage />} />
            <Route path="/parametres/debug" element={<RoleRoute roles={["admin"]}><AdminDebug /></RoleRoute>} />
            <Route path="/admin/debug" element={<LegacyDebugRedirect />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </OrganizationProvider>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}
